import type { Schemas } from "@platform/contracts/client";
import {
  PageHeader,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
  Tag,
} from "@platform/ui";
import { localName, useSession } from "@platform/ui/features";
import { useEffect, useState } from "react";
import { Link } from "react-router";
import { useTranslations } from "use-intl";
import { type Assignee, QueueFrame, QueueToolbar, TargetCell } from "../components/queue";
import { locale } from "../lib/i18n";

type Review = Schemas["OpsProductReview"];
type State = "open" | "decided";

/**
 * The product review queue, oldest first, with the entries each product matched and each review's time against the
 * 24-hour decision target, counted from its opening (CAT-5, ADM-2, card 117).
 */
export function ProductReviewsPage() {
  const t = useTranslations();
  const { session, state: auth } = useSession();
  const me = auth.status === "signed_in" ? auth.claims.sub : undefined;
  const [state, setState] = useState<State>("open");
  const [assignee, setAssignee] = useState<Assignee>("any");
  const [rows, setRows] = useState<readonly Review[] | undefined>();

  useEffect(() => {
    setRows(undefined);
    void session.api
      .GET("/v1/ops/product-reviews", { params: { query: { state, assignee } } })
      .then(({ data }) => setRows(data?.items ?? []));
  }, [session, state, assignee]);

  const assigned = (row: Review) =>
    t(
      row.assigneeOperatorId === null
        ? "admin.queue.nobody"
        : row.assigneeOperatorId === me
          ? "admin.queue.you"
          : "admin.queue.someone",
    );

  return (
    <section aria-labelledby="reviews-title" className="flex flex-col gap-5">
      <PageHeader
        id="reviews-title"
        title={t("admin.reviews.title")}
        description={t("admin.reviews.intro")}
      />
      <QueueToolbar
        id="reviews"
        states={["open", "decided"] as const}
        state={state}
        onState={setState}
        stateName={(each) => t(`admin.reviews.states.${each}`)}
        assignee={assignee}
        onAssignee={setAssignee}
      />
      <QueueFrame rows={rows}>
        <Table>
          <TableHead>
            <TableRow>
              <TableHeaderCell>{t("admin.reviews.product")}</TableHeaderCell>
              <TableHeaderCell>{t("admin.reviews.what")}</TableHeaderCell>
              <TableHeaderCell>{t("admin.reviews.matched")}</TableHeaderCell>
              <TableHeaderCell>
                {t(state === "decided" ? "admin.target.decidedColumn" : "admin.target.column")}
              </TableHeaderCell>
              <TableHeaderCell>{t("admin.queue.assignee")}</TableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows?.map((row) => (
              <TableRow key={row.id}>
                <TableCell>
                  <Link
                    className="font-medium text-ink hover:text-brand-600"
                    to={`/product-reviews/${row.id}`}
                  >
                    {localName(row.product.name, locale)}
                  </Link>
                  <span className="block text-caption text-ink-muted">
                    {t(`catalog.kind.${row.product.listingKind}`)}
                  </span>
                </TableCell>
                <TableCell>
                  <Tag
                    label={t(row.revisionId === null ? "admin.reviews.newListing" : "admin.reviews.edits")}
                  />
                </TableCell>
                <TableCell>
                  {row.matchedEntries.length === 0 ? (
                    <span className="text-ink-muted">{t(`admin.reviews.reasons.${row.reason}`)}</span>
                  ) : (
                    <span className="text-tone-attention-fg">
                      {row.matchedEntries
                        .map((entry) => `${t(`admin.risk.lists.${entry.listType}`)}: ${entry.value}`)
                        .join("; ")}
                    </span>
                  )}
                </TableCell>
                <TableCell>
                  <TargetCell startedAt={row.createdAt} decidedAt={row.decidedAt} />
                </TableCell>
                <TableCell className="text-ink-muted">{assigned(row)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </QueueFrame>
    </section>
  );
}
